import React from "react";

import { Grid, Progress } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";

import { cn } from "@/utils/cn";

import { TUsage, TUsageKey } from "@/types/TSubscription";

export const USAGE_KEYS: TUsageKey[] = [
	"projects",
	"companies",
	"clients",
	"members",
];

export interface IUsageGaugesProps {
	usage: Record<TUsageKey, TUsage>;
	className?: string;
}

const UsageRow: React.FC<{ label: string; usage: TUsage }> = ({
	label,
	usage,
}) => {
	const max = usage.max;
	const unlimited = max === null;
	const percent =
		max === null
			? 0
			: Math.min(100, Math.round((usage.used / Math.max(1, max)) * 100));
	return (
		<div className="flex flex-col gap-1" data-testid="usage-row">
			<div className="flex gap-2 text-sm">
				<span>{label}</span>
				<span className="text-neutral-500">
					{unlimited
						? `${t("subscription.usage.unlimited")}`
						: t("subscription.usage.of", {
								used: usage.used,
								max: usage.max,
							})}
				</span>
			</div>
			{!unlimited && (
				<Progress
					value={percent}
					size="sm"
					color={percent >= 100 ? "warning" : "primary"}
					label={label}
				/>
			)}
		</div>
	);
};

/** The four counted limits against the plan: a bar each, or the count alone when unlimited. */
export const UsageGauges: React.FC<IUsageGaugesProps> = ({
	usage,
	className,
}) => {
	const canShare = useFeatureFlag("sharing");

	return (
		<Grid className={cn([className])} columns={3} minItemWidth="150px">
			{USAGE_KEYS.map((key) => {
				if (key === "members" && !canShare) return null;
				return (
					<UsageRow
						key={key}
						label={t(`subscription.usage.${key}`)}
						usage={usage[key]}
					/>
				);
			})}
		</Grid>
	);
};
